import { AlertTriangle, Check, CircleHelp, X } from 'lucide-react';

import { formatarData } from '@/lib/utils';
import { mascararCns, mascararCpf } from '@/lib/mascaras';
import type { Conferencia, ItemConferencia } from '@/features/regulacao/tipos';
import { cn } from '@/lib/utils';

/**
 * Conferência lado a lado entre a solicitação e o eSUS.
 *
 * Existe para que o regulador enxergue *o que* diverge antes de confirmar, e
 * não apenas que "há divergência". Cada linha mostra os dois valores; a coluna
 * de situação combina ícone e texto, nunca só cor.
 */
export function ConferenciaEsus({ conferencia }: { conferencia: Conferencia }) {
  return (
    <div className="flex flex-col gap-3">
      {conferencia.tudoConfere ? (
        <p className="text-sucesso flex items-center gap-1.5 text-sm">
          <Check className="size-4" aria-hidden="true" />
          Os dados do paciente conferem com o eSUS.
        </p>
      ) : (
        <div
          role="alert"
          className="border-status-pendencia-borda bg-status-pendencia-fundo text-status-pendencia-texto flex items-start gap-2 rounded-md border p-3 text-sm"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <div>
            <p className="font-medium">
              {conferencia.divergencias.length === 1
                ? '1 campo diverge entre a solicitação e o eSUS.'
                : `${conferencia.divergencias.length} campos divergem entre a solicitação e o eSUS.`}
            </p>
            <p className="mt-0.5">
              {conferencia.documentoConfere
                ? 'Ao menos um documento confere, o que sugere ser a mesma pessoa com cadastro desatualizado.'
                : 'Nenhum documento confere — verifique com atenção se o ID do eSUS está correto.'}
            </p>
          </div>
        </div>
      )}

      <div className="border-borda overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <caption className="apenas-leitor-tela">
            Comparação dos dados do paciente entre a solicitação e o eSUS
          </caption>
          <thead className="bg-silenciado">
            <tr>
              <th
                scope="col"
                className="text-silenciado-texto px-3 py-2 text-left text-xs font-semibold uppercase"
              >
                Campo
              </th>
              <th
                scope="col"
                className="text-silenciado-texto px-3 py-2 text-left text-xs font-semibold uppercase"
              >
                Na solicitação
              </th>
              <th
                scope="col"
                className="text-silenciado-texto px-3 py-2 text-left text-xs font-semibold uppercase"
              >
                No eSUS
              </th>
              <th
                scope="col"
                className="text-silenciado-texto px-3 py-2 text-left text-xs font-semibold uppercase"
              >
                Situação
              </th>
            </tr>
          </thead>
          <tbody className="divide-borda divide-y">
            {conferencia.itens.map((item) => (
              <LinhaConferencia key={item.campo} item={item} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LinhaConferencia({ item }: { item: ItemConferencia }) {
  const formatar = (valor: string | null): string => {
    if (!valor) return '—';
    if (item.campo === 'cpf') return mascararCpf(valor);
    if (item.campo === 'cns') return mascararCns(valor);
    if (item.campo === 'dataNascimento') return formatarData(valor);
    return valor;
  };

  return (
    <tr className={cn(item.confere === false && 'bg-status-devolvido-fundo/40')}>
      <th scope="row" className="px-3 py-2 text-left font-medium">
        {item.rotulo}
      </th>
      <td className="px-3 py-2">{formatar(item.naSolicitacao)}</td>
      <td className="px-3 py-2">{formatar(item.noEsus)}</td>
      <td className="px-3 py-2">
        {item.confere === true && (
          <span className="text-sucesso flex items-center gap-1 text-xs">
            <Check className="size-3.5" aria-hidden="true" />
            confere
          </span>
        )}
        {item.confere === false && (
          <span className="text-destrutivo flex items-center gap-1 text-xs font-medium">
            <X className="size-3.5" aria-hidden="true" />
            diverge
          </span>
        )}
        {item.confere === null && (
          <span
            className="text-silenciado-texto flex items-center gap-1 text-xs"
            title="Um dos lados não tem esse dado, então não houve o que comparar."
          >
            <CircleHelp className="size-3.5" aria-hidden="true" />
            sem comparação
          </span>
        )}
      </td>
    </tr>
  );
}
